import React, { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import { Menu, X, LogOut, User, Settings, Bell, Sun, Moon, LayoutDashboard, BookOpen, Award, Shield, Users, Building2, FileText } from 'lucide-react'
import LanguageSwitcher from '../ui/LanguageSwitcher'

function Layout() {
  const { user, logout, hasRole } = useAuth()
  const { language, isRTL, setLanguage } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [notifications, setNotifications] = useState([])
  
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode')
    if (savedDarkMode) {
      setDarkMode(savedDarkMode === 'true')
      document.documentElement.classList.toggle('dark', savedDarkMode === 'true')
    }
  }, [])
  
  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    localStorage.setItem('darkMode', newMode)
    document.documentElement.classList.toggle('dark', newMode)
  }
  
  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }
  
  const isOfficer = hasRole(['officer', 'admin'])
  const isHR = hasRole(['company_admin', 'hr_rep', 'admin'])
  const isAdmin = hasRole(['admin'])
  
  const navigation = [
    { path: '/dashboard', label: 'dashboard.overview', icon: LayoutDashboard, roles: [] },
    { path: '/courses', label: 'courses.catalog', icon: BookOpen, roles: [] },
    { path: '/certificates', label: 'certificates.myCertificates', icon: Award, roles: [] },
    { path: '/compliance', label: 'compliance.complianceDashboard', icon: Shield, roles: ['company_admin', 'hr_rep', 'admin', 'officer'] },
    { path: '/workflows', label: 'workflows.workflowDocumentation', icon: FileText, roles: [] },
    { path: '/complaints', label: 'complaints.myComplaints', icon: FileText, roles: [] },
    
    { path: '/officer', label: 'navigation.officer', icon: Users, roles: ['officer', 'admin'] },
    { path: '/hr', label: 'navigation.hr', icon: Building2, roles: ['company_admin', 'hr_rep', 'admin'] },
    { path: '/admin', label: 'navigation.admin', icon: Settings, roles: ['admin'] },
  ]
  
  const filteredNav = navigation.filter(item => 
    item.roles.length === 0 || item.roles.some(role => hasRole(role))
  )
  
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen)
  const closeSidebar = () => setSidebarOpen(false)
  
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSidebarOpen(false)
      setMobileMenuOpen(false)
    }
  }
  
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Sidebar navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">SL</span>
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white hidden md:block">
                Sindh Labour
              </span>
            </div>
            <button
              onClick={closeSidebar}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
            {filteredNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 text-gray-600 dark:text-gray-300 
                  rounded-lg transition-colors font-medium
                  ${isActive 
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }
                `}
                onClick={closeSidebar}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          
          {/* Footer */}
          <div className="p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate capitalize">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Main content */}
      <div className="lg:pl-64 min-h-screen">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            
            {/* Page title */}
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white hidden sm:block">
                {getPageTitle(location.pathname)}
              </h1>
            </div>
            
            {/* Right side actions */}
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <div className="relative">
                <button
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                </button>
              </div>
              
              {/* Language switcher */}
              <LanguageSwitcher />
              
              {/* Theme toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              {/* User menu */}
              <div className="relative">
                <button
                  className="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="User menu"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.name}
                  </span>
                </button>
                
                {/* Dropdown */}
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</p>
                  </div>
                  <NavLink
                    to="/profile"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={closeSidebar}
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </NavLink>
                  <NavLink
                    to="/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={closeSidebar}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </NavLink>
                  <hr className="my-1 border-gray-100 dark:border-gray-700" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Main content */}
        <main className="p-4 lg:p-6" id="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function getPageTitle(pathname) {
  const titles = {
    '/dashboard': 'Dashboard',
    '/courses': 'Courses',
    '/certificates': 'Certificates',
    '/compliance': 'Compliance',
    '/workflows': 'Workflows',
    '/complaints': 'Complaints',
    '/officer': 'Officer Portal',
    '/hr': 'HR Portal',
    '/admin': 'Admin Panel',
    '/profile': 'Profile',
    '/settings': 'Settings',
  }
  
  for (const [path, title] of Object.entries(titles)) {
    if (pathname.startsWith(path)) return title
  }
  return 'Dashboard'
}

export default Layout